
import React, { useState, useEffect, useRef } from "react";

interface StreamingMessageProps {
  content: string;
  isComplete: boolean;
  onComplete?: () => void;
}

const StreamingMessage: React.FC<StreamingMessageProps> = ({ 
  content, 
  isComplete,
  onComplete
}) => {
  const [displayedContent, setDisplayedContent] = useState("");
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const completedRef = useRef(false);
  
  // Clean the content before processing to remove any system instructions
  const cleanContent = (rawContent: string): string => {
    if (!rawContent) return '';
    
    let cleaned = rawContent;
    
    // Remove system context blocks that might leak through
    cleaned = cleaned.replace(/SYSTEM CONTEXT[\s\S]*?(?=\n\n[A-ZÆØÅ]|$)/gi, '');
    cleaned = cleaned.replace(/DO NOT SHOW TO USER:[\s\S]*?(?=\n\n|$)/gi, '');
    cleaned = cleaned.replace(/VIKTIG:[\s\S]*?fra API\.[\s\S]*?(?=\n\n|$)/gi, '');
    
    // Remove backend instructions
    cleaned = cleaned.replace(/Følgende data er hentet fra offisielle registre[\s\S]*?(?=\n\n|$)/gi, '');
    cleaned = cleaned.replace(/Presenter kun denne informasjonen[\s\S]*?(?=\n\n|$)/gi, '');
    
    // Remove emojis completely
    cleaned = cleaned.replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '');
    
    // Clean up excess whitespace
    cleaned = cleaned.replace(/\n\s*\n\s*\n+/g, '\n\n');
    cleaned = cleaned.trim();
    
    return cleaned;
  };
  
  const cleanedContent = cleanContent(content);
  const words = cleanedContent.split(/(\s+)/);
  const wordSpeed = 80; // Reduced from 150ms to 80ms for faster streaming

  // Reset animation state when content changes completely
  useEffect(() => {
    completedRef.current = false;
    setAnimationComplete(false);
    setCurrentWordIndex(0);
    setDisplayedContent("");
  }, [cleanedContent]);

  // Handle streaming or display full content
  useEffect(() => {
    // If the response is complete, show the entire content immediately
    if (isComplete && !animationComplete) {
      setDisplayedContent(cleanedContent);
      setAnimationComplete(true);
      if (onComplete && !completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
      return;
    }

    // Handle word-by-word streaming
    if (!animationComplete && currentWordIndex < words.length) {
      const timer = setTimeout(() => {
        const wordsToShow = words.slice(0, currentWordIndex + 1);
        setDisplayedContent(wordsToShow.join(''));
        setCurrentWordIndex(prev => prev + 1);
      }, wordSpeed);
      
      return () => clearTimeout(timer);
    } else if (currentWordIndex >= words.length && !completedRef.current) {
      setAnimationComplete(true);
      if (onComplete) {
        completedRef.current = true;
        onComplete();
      }
    }
  }, [cleanedContent, currentWordIndex, isComplete, onComplete, animationComplete, words]);

  return (
    <div 
      className="whitespace-pre-wrap w-full text-base leading-relaxed"
      dangerouslySetInnerHTML={{ __html: displayedContent }}
    />
  );
};

export default StreamingMessage;
